import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

export default function Nav() {
  const { t, lang, setLang } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // lock body scroll when mobile drawer open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const textColor = scrolled ? "text-[#211512]" : "text-[#FFF8EE]";

  const links = [
    { label: t("nav.story"), href: "#story", id: "story" },
    { label: t("nav.bases"), href: "#bases", id: "bases" },
    { label: t("nav.menu"), href: "#menu", id: "menu" },
    { label: t("nav.contact"), href: "#contact", id: "contact" },
  ];

  return (
    <>
      <motion.header
        data-testid="tirami-nav"
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || mobileOpen ? "nav-blurred" : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 flex items-center justify-between h-[78px]">
          <a
            href="#top"
            data-testid="nav-logo"
            onClick={() => setMobileOpen(false)}
            className={`font-logo text-3xl tracking-tight ${
              mobileOpen ? "text-[#211512]" : textColor
            } transition-colors duration-500`}
          >
            TIRAMI
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.href}
                data-testid={`nav-link-${l.id}`}
                className={`label-tracked underline-grow ${textColor} transition-colors duration-500`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-4 md:gap-6">
            <LangSwitch
              lang={lang}
              setLang={setLang}
              scrolled={scrolled || mobileOpen}
            />

            {/* Mobile hamburger */}
            <button
              type="button"
              data-testid="nav-mobile-toggle"
              onClick={() => setMobileOpen((p) => !p)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              className={`md:hidden inline-flex items-center justify-center w-10 h-10 border ${
                mobileOpen
                  ? "border-[#211512] text-[#211512]"
                  : `border-current ${textColor}`
              } transition-colors duration-500`}
            >
              {mobileOpen ? (
                <X className="w-4 h-4" strokeWidth={1.5} />
              ) : (
                <MenuIcon className="w-4 h-4" strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mobile-drawer"
            data-testid="nav-mobile-drawer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 md:hidden"
            style={{ background: "#FFF8EE" }}
          >
            <motion.nav
              initial={{ y: -16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -8, opacity: 0 }}
              transition={{ duration: 0.35, delay: 0.05 }}
              className="h-full pt-[100px] px-8 pb-12 flex flex-col"
            >
              <ul className="space-y-6 mt-6">
                {links.map((l, i) => (
                  <motion.li
                    key={l.id}
                    initial={{ x: -16, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.1 + i * 0.06 }}
                  >
                    <a
                      href={l.href}
                      data-testid={`nav-mobile-link-${l.id}`}
                      onClick={() => setMobileOpen(false)}
                      className="block font-serif text-[#211512] leading-none"
                      style={{ fontSize: "clamp(2.25rem, 9vw, 3.5rem)" }}
                    >
                      {l.label}
                    </a>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-auto pt-12 border-t border-[#211512]/15 flex items-center justify-between">
                <p className="label-tracked text-[#7B5C4E]">
                  TIRAMI — Monterrey
                </p>
                <div
                  className="flex items-center gap-3"
                  data-testid="nav-mobile-lang"
                >
                  <button
                    type="button"
                    onClick={() => setLang("en")}
                    data-testid="nav-mobile-lang-en"
                    aria-pressed={lang === "en"}
                    className={`label-tracked text-xs transition-opacity ${
                      lang === "en"
                        ? "opacity-100 font-semibold"
                        : "opacity-50"
                    } text-[#211512]`}
                  >
                    EN
                  </button>
                  <span className="w-px h-3 bg-[#211512]/30" aria-hidden />
                  <button
                    type="button"
                    onClick={() => setLang("es")}
                    data-testid="nav-mobile-lang-es"
                    aria-pressed={lang === "es"}
                    className={`label-tracked text-xs transition-opacity ${
                      lang === "es"
                        ? "opacity-100 font-semibold"
                        : "opacity-50"
                    } text-[#211512]`}
                  >
                    ES
                  </button>
                </div>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function LangSwitch({ lang, setLang, scrolled }) {
  const textColor = scrolled ? "text-[#211512]" : "text-[#FFF8EE]";
  const dividerColor = scrolled ? "bg-[#211512]/30" : "bg-[#FFF8EE]/30";

  const buttonClass = (active) =>
    `label-tracked text-[10px] transition-all duration-300 ${
      active ? "opacity-100 font-semibold" : "opacity-50 hover:opacity-100"
    } ${textColor}`;

  return (
    <div
      className={`hidden sm:flex items-center gap-2.5 px-1 ${textColor}`}
      data-testid="nav-lang-switch"
      role="group"
      aria-label="Language switch"
    >
      <button
        type="button"
        onClick={() => setLang("en")}
        data-testid="nav-lang-en"
        aria-pressed={lang === "en"}
        className={buttonClass(lang === "en")}
      >
        EN
      </button>
      <span className={`w-px h-3 ${dividerColor}`} aria-hidden />
      <button
        type="button"
        onClick={() => setLang("es")}
        data-testid="nav-lang-es"
        aria-pressed={lang === "es"}
        className={buttonClass(lang === "es")}
      >
        ES
      </button>
    </div>
  );
}
